import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { analyticsApi } from '@/lib/api'
import { cn } from '@/lib/utils'
import { cardTitleStyle } from '@/lib/styleKit'
import { EmptyChart } from './EmptyChart'
import { UsersThree } from '@phosphor-icons/react'
import {
  AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts'
import { useReducedMotion } from 'framer-motion'
import { DotLoader } from '@/components/ui/DotLoader'
import {
  BRAND_SERIES_COLOR,
  ENGAGEMENT_COLORS,
  ENGAGEMENT_FALLBACK,
  sequentialStep,
  tooltipContentStyle,
  useChartTheme,
} from './chartTheme'
import type { ContactsData, Period } from '@/types/analytics'

interface TabProps {
  period: Period | null
  startDate: string | null
  endDate: string | null
  userId: number | null
}

// The engagement scale and the pipeline ramp used to be declared here. Both now
// come from `chartTheme` (§61): the funnel is **ordinal**, so it takes the
// ordinal indigo ramp rather than the old
// `['#5e6ad2','#7170ff','#a5b4fc','#f5a524','#10b981','#8a8f98']` — six
// categorical hues on ranked data, whose first two were deltaE 7.4 apart, so two
// adjacent funnel bars were indistinguishable even to full-colour readers.

const cardCls = cn(
  'rounded-xl border transition-colors duration-150',
  'bg-card border-border/50',
  'dark:bg-white/[0.02] dark:border-white/[0.08]',
)

// Pill chrome — single-select per §48
const pillIdle =
  'bg-muted/50 text-muted-foreground border-border/50 hover:bg-muted/70 hover:border-border/80 dark:bg-white/[0.04] dark:text-[#8a8f98] dark:border-white/[0.06] dark:hover:bg-white/[0.06] dark:hover:border-white/[0.10]'
const pillActive =
  'bg-[#5e6ad2]/12 text-[#5e6ad2] border-[#5e6ad2]/30 dark:bg-[#5e6ad2]/[0.18] dark:text-[#a5b4fc] dark:border-[#5e6ad2]/40'

export default function ContactsTab({ period, startDate, endDate, userId }: TabProps) {
  const chartTheme = useChartTheme()
  // Recharts animates via react-smooth (JS-driven), so it ignores both the global
  // CSS reduced-motion guard and framer-motion's <MotionConfig>. Gate it explicitly.
  const prefersReduced = useReducedMotion()
  const [segmentView, setSegmentView] = useState<'industry' | 'job_title'>('industry')

  const { data, isLoading } = useQuery<ContactsData>({
    queryKey: ['analytics', 'contacts', period, startDate, endDate, userId],
    queryFn: () =>
      analyticsApi
        .getContacts({ period: period || undefined, start: startDate || undefined, end: endDate || undefined, user_id: userId ?? undefined })
        .then((r) => r.data),
    staleTime: 5 * 1000,
    refetchOnWindowFocus: true,
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24 text-[#5e6ad2] dark:text-[#a5b4fc]">
        <DotLoader size="lg" label="Loading contact data" />
      </div>
    )
  }

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center max-w-md mx-auto">
        <div className="h-12 w-12 rounded-md bg-muted/70 dark:bg-white/[0.04] dark:border dark:border-white/[0.05] flex items-center justify-center mb-4">
          <UsersThree className="h-5 w-5 text-muted-foreground dark:text-[#8a8f98]" weight="regular" />
        </div>
        <h3
          className="text-foreground dark:text-[#f7f8f8] mb-2"
          style={{ fontSize: '20px', fontWeight: 590, letterSpacing: '-0.24px', lineHeight: 1.33 }}
        >
          No contact data
        </h3>
        <p
          className="text-muted-foreground dark:text-[#8a8f98] leading-relaxed"
          style={{ fontSize: '13px', letterSpacing: '-0.13px' }}
        >
          No contact data available for this period.
        </p>
      </div>
    )
  }

  const maxEngagement = Math.max(...data.engagement_distribution.map((d) => d.count), 1)
  const segments = segmentView === 'industry' ? data.top_segments.by_industry : data.top_segments.by_job_title

  return (
    <div className="space-y-5">
      {/* Row 1: Pipeline Funnel + Contact Growth */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card className={cardCls}>
          <CardHeader>
            <CardTitle className="text-foreground dark:text-[#f7f8f8]" style={cardTitleStyle}>
              Pipeline funnel
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.pipeline.stages.length > 0 ? (
              <div className="space-y-4">
                <div className="space-y-2.5">
                  {data.pipeline.stages.map((stage, i) => {
                    // Width is the funnel percentage itself (relative to the top
                    // stage) — no minimum floor, so a 0% stage renders empty.
                    const widthPct = Math.min(Math.max(stage.pct, 0), 100)
                    return (
                      <div key={stage.name} className="flex items-center gap-3">
                        <span
                          className="text-muted-foreground dark:text-[#8a8f98] w-24 flex-shrink-0 text-right capitalize truncate"
                          style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
                        >
                          {stage.name.replace(/_/g, ' ')}
                        </span>
                        <div
                          className="relative flex-1 min-w-0 h-7 rounded-md overflow-hidden bg-muted/40 dark:bg-white/[0.04]"
                          aria-hidden="true"
                        >
                          <div
                            className="h-full rounded-md transition-all duration-300"
                            style={{
                              width: `${widthPct}%`,
                              backgroundColor: sequentialStep(chartTheme, i, data.pipeline.stages.length),
                            }}
                          />
                        </div>
                        {/* Value sits in its own column outside the track. On the
                            track it collided with the fill as soon as a stage
                            approached 100% — dark ramp step under dark text,
                            below the 4.5:1 floor and visually overlapping the
                            bar end. Outside, it is legible at every width and
                            the numbers line up down the column. */}
                        <span
                          className="flex items-baseline justify-end gap-1.5 w-[104px] flex-shrink-0 whitespace-nowrap"
                        >
                          <span
                            className="text-foreground dark:text-[#f7f8f8] tabular-nums"
                            style={{ fontSize: '12px', fontWeight: 590, letterSpacing: '-0.1px' }}
                          >
                            {stage.count.toLocaleString()}
                          </span>
                          <span
                            className="text-muted-foreground dark:text-[#8a8f98] tabular-nums"
                            style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.05px' }}
                          >
                            {stage.pct.toFixed(1)}%
                          </span>
                        </span>
                      </div>
                    )
                  })}
                </div>
                {data.pipeline.conversions.length > 0 && (
                  <div className="border-t border-border/50 dark:border-white/[0.06] pt-3 space-y-1">
                    <p
                      className="text-muted-foreground dark:text-[#8a8f98] uppercase mb-1.5"
                      style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.06em' }}
                    >
                      Conversion rates
                    </p>
                    {data.pipeline.conversions.map((c) => (
                      <div
                        key={`${c.from}-${c.to}`}
                        className="flex items-center justify-between rounded-md px-2 py-1.5 hover:bg-muted/40 dark:hover:bg-white/[0.04] transition-colors duration-150"
                      >
                        <span
                          className="text-muted-foreground dark:text-[#8a8f98] capitalize"
                          style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                        >
                          {c.from.replace(/_/g, ' ')} → {c.to.replace(/_/g, ' ')}
                        </span>
                        <span
                          className="text-foreground dark:text-[#f7f8f8] tabular-nums"
                          style={{ fontSize: '12px', fontWeight: 590, letterSpacing: '-0.1px' }}
                        >
                          {c.rate.toFixed(1)}%
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <EmptyChart message="No pipeline data yet." icon={<UsersThree className="h-5 w-5" weight="regular" />} />
            )}
          </CardContent>
        </Card>

        <Card className={cardCls}>
          <CardHeader>
            <CardTitle className="text-foreground dark:text-[#f7f8f8]" style={cardTitleStyle}>
              Contact growth
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.growth.data_points.length > 0 ? (
              // `role="group"`, not a bare labelled div: `aria-label` on a
              // generic div is dropped by AT, and `role="img"` would hide the
              // series `accessibilityLayer` just made explorable.
              <div role="group" aria-label="Total contacts over time">
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart accessibilityLayer data={data.growth.data_points} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="growthGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor={BRAND_SERIES_COLOR} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={BRAND_SERIES_COLOR} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.grid} vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: chartTheme.tickFill }} stroke={chartTheme.axis} strokeWidth={1} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: chartTheme.tickFill }} stroke={chartTheme.axis} strokeWidth={1} tickLine={false} axisLine={false} />
                    <Tooltip
                      cursor={{ stroke: chartTheme.axis, strokeDasharray: '3 3' }}
                      contentStyle={tooltipContentStyle(chartTheme)}
                      labelStyle={{ color: chartTheme.tooltipText, fontWeight: 590, marginBottom: 4 }}
                    />
                    <Area type="monotone" dataKey="total" name="Total contacts" stroke={BRAND_SERIES_COLOR} strokeWidth={2} fill="url(#growthGradient)" isAnimationActive={!prefersReduced} />
                  </AreaChart>
                </ResponsiveContainer>
                <div className="grid grid-cols-3 gap-2.5 mt-4 pt-4 border-t border-border/50 dark:border-white/[0.06]">
                  {[
                    { label: 'New contacts', value: data.growth.new_contacts,   tone: 'default' as const },
                    { label: 'Bounced',      value: data.growth.bounced,        tone: 'red'     as const },
                    { label: 'Unsubscribed', value: data.growth.unsubscribed,   tone: 'amber'   as const },
                  ].map(({ label, value, tone }) => (
                    <div
                      key={label}
                      className={cn(
                        'text-center rounded-lg p-3 border',
                        'bg-muted/30 border-border/50',
                        'dark:bg-white/[0.02] dark:border-white/[0.06]',
                      )}
                    >
                      <div
                        className={cn(
                          'tabular-nums',
                          tone === 'red'   && 'text-red-600 dark:text-red-400',
                          tone === 'amber' && 'text-amber-600 dark:text-amber-400',
                          tone === 'default' && 'text-foreground dark:text-[#f7f8f8]',
                        )}
                        style={{ fontSize: '17px', fontWeight: 590, letterSpacing: '-0.182px', lineHeight: 1.1 }}
                      >
                        {value.toLocaleString()}
                      </div>
                      <div
                        className="text-muted-foreground dark:text-[#8a8f98] uppercase mt-1"
                        style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.06em' }}
                      >
                        {label}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <EmptyChart message="No growth data yet." icon={<UsersThree className="h-5 w-5" weight="regular" />} />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Row 2: Engagement Distribution + Top Segments */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card className={cardCls}>
          <CardHeader>
            <CardTitle className="text-foreground dark:text-[#f7f8f8]" style={cardTitleStyle}>
              Engagement distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.engagement_distribution.length > 0 ? (
              <div className="space-y-3.5">
                {data.engagement_distribution.map((d) => {
                  // No minimum floor. `Math.max(..., 4)` used to give a genuine
                  // zero a visible 4% bar — the same lie the funnel and the
                  // segment bars in this file already removed.
                  const widthPct = (d.count / maxEngagement) * 100
                  const color = ENGAGEMENT_COLORS[d.level] || ENGAGEMENT_FALLBACK
                  return (
                    <div key={d.level} className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span
                          className="text-foreground dark:text-[#f7f8f8]"
                          style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
                        >
                          {d.label}
                        </span>
                        <span
                          className="text-muted-foreground dark:text-[#8a8f98] tabular-nums"
                          style={{ fontSize: '11px', letterSpacing: '-0.1px' }}
                        >
                          {d.count.toLocaleString()}
                        </span>
                      </div>
                      <div className="h-2 rounded-full overflow-hidden bg-muted/50 dark:bg-white/[0.06]">
                        <div
                          className="h-full rounded-full transition-all duration-300"
                          style={{ width: `${widthPct}%`, backgroundColor: color }}
                        />
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <EmptyChart message="No engagement data yet." icon={<UsersThree className="h-5 w-5" weight="regular" />} />
            )}
          </CardContent>
        </Card>

        <Card className={cardCls}>
          <CardHeader>
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="text-foreground dark:text-[#f7f8f8]" style={cardTitleStyle}>
                Top segments
              </CardTitle>
              <div className="flex gap-1.5">
                {(['industry', 'job_title'] as const).map((view) => (
                  <button
                    key={view}
                    type="button"
                    onClick={() => setSegmentView(view)}
                    aria-pressed={segmentView === view}
                    className={cn(
                      'inline-flex items-center px-2.5 py-1 rounded-full border transition-colors duration-150',
                      segmentView === view ? pillActive : pillIdle,
                    )}
                    style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
                  >
                    {view === 'industry' ? 'Industry' : 'Job title'}
                  </button>
                ))}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {segments.length > 0 ? (
              <div className="space-y-3.5">
                {segments.map((seg) => {
                  // Absolute reply rate (0-100), no minimum floor — a 0.0%
                  // segment renders an empty bar.
                  const widthPct = Math.min(Math.max(seg.reply_rate, 0), 100)
                  return (
                    <div key={seg.name} className="space-y-1.5">
                      <div className="flex items-center justify-between gap-2">
                        <span
                          className="text-foreground dark:text-[#f7f8f8] truncate"
                          style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
                        >
                          {seg.name}
                        </span>
                        <span
                          className="text-muted-foreground dark:text-[#8a8f98] whitespace-nowrap tabular-nums"
                          style={{ fontSize: '11px', letterSpacing: '-0.1px' }}
                        >
                          {seg.count.toLocaleString()} {seg.count === 1 ? 'contact' : 'contacts'} · {seg.reply_rate.toFixed(1)}% reply
                        </span>
                      </div>
                      <div className="h-2 rounded-full overflow-hidden bg-muted/50 dark:bg-white/[0.06]">
                        <div
                          className="h-full rounded-full transition-all duration-300 bg-[#5e6ad2]/80 dark:bg-[#7170ff]/80"
                          style={{ width: `${widthPct}%` }}
                        />
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <EmptyChart message="No segment data yet." icon={<UsersThree className="h-5 w-5" weight="regular" />} />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
